import interpolateValue from '../../utils/interpolateValue.js';
import { formatAttributeKey } from '../formatAttributeKey.js';
import { escapeMethods } from './escapeMethods.js';
import { replaceArgumentValue } from './replaceArgumentValue.js';
import { traceInterpolationIndices } from './traceInterpolationIndices.js';
import { Buffer as BufferPolyfill } from 'buffer';

export const serializeLexical = (contentState, responses, opts = {}) => {
  const traceSet = [];
  const set = [];

  const escapeArguments = opts.escapeArguments;
  const format = opts?.format;
  const captureSingleReferenceArray = opts.captureSingleReferenceArray === true && !opts.ai && !format && !opts.systemToHuman && !escapeArguments?.enabled && !responses?.interpolationContext;
  let singleReferenceArray;

  const recurse = (children) => {
    children.forEach((child) => {
      if (child.type === 'text') {
        let value = child.text;

        if (escapeArguments?.enabled && !format) {
          value = escapeMethods[escapeArguments.type]?.(value, escapeArguments.options);
        }

        traceSet.push(undefined);
        set.push(value);
      } else if (child.type === 'linebreak') {
        traceSet.push(undefined);
        set.push('\n');
      } else if (child.type === 'block') {
        const { stepId, key, map } = child.data;
        let value;
        if (opts?.ai === true) {
          value = `[${stepId}:${key}]`;
        } else if (typeof opts?.renderBlock === 'function') {
          // The caller prints the reference itself (the public API writes it as a {{ }} template).
          value = opts.renderBlock(child.data);
        } else {
          value = replaceArgumentValue(responses)(stepId, key, opts, map);
        }

        if (captureSingleReferenceArray && Array.isArray(value) && Array.from(value).every((item) => ['string', 'number', 'boolean'].includes(typeof item))) {
          singleReferenceArray = value;
        }

        // Sometimes we return a Date object that ends up in the following if block, this is to prevent that
        // and than we cast to string so we return the expected value.
        const isDateObject = value instanceof Date;
        const isBuffer = BufferPolyfill.isBuffer(value);
        const dataType = isBuffer ? 'binary' : isDateObject ? 'date' : Array.isArray(value) ? 'array' : typeof value;

        if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value || {})?.length === 0 && !isDateObject && !isBuffer) {
          value = undefined;
        }
        const shouldStringify = typeof value === 'object' && !isDateObject && !isBuffer;

        if (shouldStringify) {
          value = JSON.stringify(value);
        }

        if (escapeArguments?.enabled && !format) {
          value = escapeMethods[escapeArguments.type]?.(value, escapeArguments.options);
        }

        if (format === 'human') {
          const pattern = /^(response|input)\[\d+\]\./;
          const cleanedKey = key.replace(pattern, '');
          value = formatAttributeKey(cleanedKey);
        }

        traceSet.push({ stepId, key, replacedValue: value, text: `{{${key}}}`, stringified: shouldStringify, dataType });
        set.push(value);
      } else if (child.children) {
        recurse(child.children);
      }
    });
  };

  const rootChildren = contentState?.root?.children || [];
  rootChildren.forEach((n, index) => {
    if (n.type === 'paragraph' && n.children) {
      recurse(n.children);
      if (index < rootChildren.length - 1) {
        traceSet.push(undefined);
        set.push('\n'); // Add a newline only if it's not the last paragraph
      }
    }
  });

  let value = traceSet[0]?.dataType === 'binary' ? set[0] : set.join('');
  const indices = traceInterpolationIndices(set, traceSet);

  if (responses?.interpolationContext) {
    value = interpolateValue(value, responses.interpolationContext);
  }

  // Keep text and trace offsets stable; only the collection resolver requests
  // the native value. Empty text nodes are inert; whitespace and other references are not.
  if (singleReferenceArray && traceSet.filter(Boolean).length === 1 && set.every((part, index) => traceSet[index] !== undefined || part === '')) {
    return { value, indices, singleReferenceArray };
  }

  return { value, indices };
};
